/* eslint-disable camelcase */
'use client';

import { useEffect } from 'react';

interface CookiebotConsent {
  preferences?: boolean;
  statistics?: boolean;
  marketing?: boolean;
}

declare global {
  interface Window {
    Cookiebot?: { consent?: CookiebotConsent };
  }
}

/**
 * Loads Google Tag Manager and drives Google Consent Mode v2.
 *
 * On mount it sets the defaults (everything denied) BEFORE Tag Manager is
 * injected, so every Google tag starts privacy-safe, then forwards the
 * visitor's Cookiebot choice as a consent update.
 *
 * Tag Manager is injected immediately after hydration. The script is async, so
 * it never blocks the first paint, and injecting it straight away means the
 * GA4 page_view fires while the visitor is still on the page. (It was
 * previously deferred until after the window 'load' event AND
 * requestIdleCallback, which silently dropped page_views for fast-leaving and
 * mobile visitors who left before the container ever loaded.) The script is
 * marked data-cookieconsent="ignore" so Cookiebot's auto-blocking never
 * suppresses it.
 */

const GTM_ID = 'GTM-MDPGFGK2';
const GTM_SCRIPT_ELEMENT_ID = 'gtm-loader-script';

const COOKIEBOT_CONSENT_EVENTS = [
  'CookiebotOnLoad',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

const CONSENT_MODE_DEFAULTS = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
  wait_for_update: 500,
} as const;

type GoogleTagCommand = (...args: unknown[]) => void;

const toArgumentsObject = function (): IArguments {
  return arguments;
};

type ConsentState = 'granted' | 'denied';

const toConsentState = (allowed: boolean | undefined): ConsentState =>
  allowed ? 'granted' : 'denied';

const toConsentUpdate = (consent: CookiebotConsent) => ({
  analytics_storage: toConsentState(consent.statistics),
  ad_storage: toConsentState(consent.marketing),
  ad_user_data: toConsentState(consent.marketing),
  ad_personalization: toConsentState(consent.marketing),
  functionality_storage: toConsentState(consent.preferences),
  personalization_storage: toConsentState(consent.preferences),
  security_storage: 'granted' as const,
});

export const GtmLoader = () => {
  const enabled = process.env.NEXT_PUBLIC_GTM_ENABLED === 'true';

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer ?? [];
    const dataLayer = window.dataLayer as unknown[];

    const gtag: GoogleTagCommand = (...args) =>
      dataLayer.push(toArgumentsObject(...args));

    gtag('consent', 'default', CONSENT_MODE_DEFAULTS);
    gtag('set', 'ads_data_redaction', true);
    gtag('set', 'url_passthrough', false);

    const forwardCookiebotConsent = () => {
      const consent = window.Cookiebot?.consent;
      if (!consent) return;
      gtag('consent', 'update', toConsentUpdate(consent));
    };
    forwardCookiebotConsent();

    const consentSubscription = new AbortController();
    COOKIEBOT_CONSENT_EVENTS.forEach((event) => {
      window.addEventListener(event, forwardCookiebotConsent, {
        signal: consentSubscription.signal,
      });
    });

    const injectTagManagerScript = () => {
      if (document.getElementById(GTM_SCRIPT_ELEMENT_ID)) return;

      dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

      const script = document.createElement('script');
      script.id = GTM_SCRIPT_ELEMENT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      script.setAttribute('data-cookieconsent', 'ignore');
      document.head.appendChild(script);
    };

    injectTagManagerScript();

    return () => {
      consentSubscription.abort();
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <noscript>
      <iframe
        title="Google Tag Manager"
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
};
